/**
 * StaleConflictDialog — Task 7.3 (F-2.0 Phase 7)
 *
 * Shown when:
 *   A) POST /orders/:id/proposal returns 409 {error:"stale_version", current_version:N}
 *      → non-blocking diff: user's items vs server's latest revision
 *   B) POST /orders/:id/proposal returns 422 {error:"stale_item_price", items:[...]}
 *      → highlights stale rows inline (borda vermelha + ⚠️ ícone)
 *
 * Decisions (plan Phase 7):
 *   - 409: side-by-side view; "Adoptar versão servidor" | "Manter as minhas alterações (forçar)"
 *   - 422: list of stale items with old vs new cost; user must acknowledge before re-saving
 *   - Never silently discard user's work
 */
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Chip,
  Alert,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { ProposalDraftItem, ProposalRevision } from "../../model/Proposal";

// ── shared helper ──────────────────────────────────────────────────────────────

function fmt(cents: number): string {
  return (cents / 100).toFixed(2) + " €";
}

// ── 409 Stale Version Dialog ──────────────────────────────────────────────────

export interface StaleVersionDialogProps {
  open: boolean;
  /** Items the user had in the editor */
  userItems: ProposalDraftItem[];
  userNotes: string;
  userTotalCents: number;
  /** Server's latest revision (fetched after 409) */
  serverRevision: ProposalRevision | null;
  onAdoptServer: () => void;
  onKeepMine: (forcedVersion: number) => void;
  onClose: () => void;
}

export const StaleVersionDialog: React.FC<StaleVersionDialogProps> = ({
  open,
  userItems,
  userNotes,
  userTotalCents,
  serverRevision,
  onAdoptServer,
  onKeepMine,
  onClose,
}) => {
  const serverItems: ProposalDraftItem[] = React.useMemo(() => {
    if (!serverRevision?.items) return [];
    return serverRevision.items.map((i) => ({
      ...i,
      _localId: crypto.randomUUID(),
    }));
  }, [serverRevision]);

  const serverVersion = serverRevision?.version ?? 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{ bgcolor: "warning.main", color: "warning.contrastText" }}
      >
        ⚠️ Conflito de versão — a tua revisão está desactualizada
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Alguém guardou uma revisão mais recente (v{serverVersion}) entretanto.
          Compara as diferenças e escolhe como continuar.
        </Alert>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
          {/* User side */}
          <Box>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              As tuas alterações
            </Typography>
            <ItemsTable items={userItems} />
            <Typography variant="body2" mt={1} color="text.secondary">
              Total: <strong>{fmt(userTotalCents)}</strong>
            </Typography>
            {userNotes && (
              <Typography variant="body2" mt={0.5} color="text.secondary">
                Notas: {userNotes}
              </Typography>
            )}
          </Box>

          {/* Server side */}
          <Box>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              Versão do servidor (v{serverVersion})
            </Typography>
            {serverRevision ? (
              <>
                <ItemsTable items={serverItems} />
                <Typography variant="body2" mt={1} color="text.secondary">
                  Total: <strong>{fmt(serverRevision.total_cents)}</strong>
                </Typography>
                {serverRevision.notes && (
                  <Typography variant="body2" mt={0.5} color="text.secondary">
                    Notas: {serverRevision.notes}
                  </Typography>
                )}
              </>
            ) : (
              <Typography color="text.secondary" variant="body2">
                A carregar revisão do servidor…
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancelar
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onAdoptServer}
            disabled={!serverRevision}
          >
            Adoptar versão servidor
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => onKeepMine(serverVersion)}
            disabled={!serverRevision}
          >
            Manter as minhas alterações
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

// ── 422 Stale Item Price Dialog ───────────────────────────────────────────────

export interface StaleItemPrice {
  product_id: string;
  name: string;
  old_cost_cents: number;
  new_cost_cents: number;
}

export interface StaleItemPriceDialogProps {
  open: boolean;
  staleItems: StaleItemPrice[];
  onAcknowledge: () => void;
  onClose: () => void;
}

export const StaleItemPriceDialog: React.FC<StaleItemPriceDialogProps> = ({
  open,
  staleItems,
  onAcknowledge,
  onClose,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: "error.main", color: "error.contrastText" }}>
        <WarningAmberIcon sx={{ verticalAlign: "middle", mr: 1 }} />
        Preços desactualizados
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="error" sx={{ mb: 2 }}>
          O preço de {staleItems.length} componente(s) mudou desde que
          carregaste o editor. Confirma os valores actualizados antes de
          guardar.
        </Alert>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Produto</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Custo anterior</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Custo actual</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Δ</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {staleItems.map((item) => {
              const delta = item.new_cost_cents - item.old_cost_cents;
              const isIncrease = delta > 0;
              return (
                <TableRow key={item.product_id}>
                  <TableCell>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <WarningAmberIcon
                        sx={{ fontSize: 16, color: "error.main" }}
                      />
                      {item.name}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ color: "text.secondary" }}>
                    {fmt(item.old_cost_cents)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {fmt(item.new_cost_cents)}
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${isIncrease ? "+" : ""}${fmt(delta)}`}
                      color={isIncrease ? "error" : "success"}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Rever manualmente
        </Button>
        <Button variant="contained" color="error" onClick={onAcknowledge}>
          Confirmo — guardar com preços actualizados
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── shared sub-component ──────────────────────────────────────────────────────

const ItemsTable: React.FC<{ items: ProposalDraftItem[] }> = ({ items }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>
          <strong>Produto</strong>
        </TableCell>
        <TableCell align="right">Qty</TableCell>
        <TableCell align="right">PVP</TableCell>
        <TableCell align="right">Custo</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {items.map((item, idx) => (
        <TableRow key={item.product_id || idx}>
          <TableCell>{item.name || item.product_id}</TableCell>
          <TableCell align="right">{item.quantity}</TableCell>
          <TableCell align="right">{fmt(item.pvp_cents)}</TableCell>
          <TableCell align="right">{fmt(item.cost_cents)}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);
